import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Toolbar,
  Button,
  CircularProgress,
  Chip,
  useTheme,
} from "@mui/material";
import HomeHeader from "../../../../Components/HomeHeader.js";
import API from "../../../../API/index.js";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const CreatorCampaignTimelines = () => {
  const { campaignId, creatorId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const { isLoading, data: timelineFetch, error, isError } = useQuery({
    queryKey: ['campaign', campaignId, 'timeline', creatorId],
    queryFn: () => API.campaigns.timeline_by_creator(campaignId, creatorId),
    refetchInterval: false,
    refetchOnMount: 'always',
    onError: (error) => { alert(`Error: ${(error.response && error.response.data) ? error.response.data.error : error.message}`); }
  });

  const timelineData = useMemo(() => {
    if (timelineFetch === undefined || timelineFetch === null) return [];
    return timelineFetch.events.map((event) => ({
      ...event,
      status: event.status || "incomplete",
    }));
  }, [timelineFetch]);

  const campaignName = useMemo(() => {
    if (timelineFetch === undefined || timelineFetch === null) return 'Loading';
    return timelineFetch.campaignName;
  }, [timelineFetch]);

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'complete':
        return <CheckCircleIcon color="success" sx={{ fontSize: '2rem' }} />;
      case 'in progress':
        return <PendingIcon color="warning" sx={{ fontSize: '2rem' }} />;
      default:
        return <AccessTimeIcon color="disabled" sx={{ fontSize: '2rem' }} />;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: 'background.paper',
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
        textAlign: 'center',
        px: 2,
      }}>
        <Typography variant="h5" color="error">
          Failed to load timeline data
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <>
      <HomeHeader />
      <Toolbar />
      
      <Box sx={{
        maxWidth: 1200,
        mx: 'auto',
        px: { xs: 2, md: 4 },
        py: 4,
      }}>
        <Box sx={{
          textAlign: 'center',
          mb: 6,
          '& h1': {
            fontWeight: 700,
            letterSpacing: '-0.05rem',
            mb: 2,
          },
          '& p': {
            color: 'text.secondary',
            maxWidth: 600,
            mx: 'auto',
          }
        }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Campaign Timeline
          </Typography>
          <Typography variant="subtitle1">
            {campaignName} with @{creatorId}
          </Typography>
        </Box>

        {timelineData.length === 0 ? (
          <Box sx={{
            textAlign: 'center',
            py: 8,
            borderRadius: 4,
            backgroundColor: 'background.default',
          }}>
            <Typography variant="h6" color="text.secondary">
              No timelines found for this campaign
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: { xs: 20, sm: 30 },
              top: 0,
              height: '100%',
              width: 2,
              backgroundColor: 'divider',
            }
          }}>
            {timelineData.map((event, index) => (
              <Box key={index} sx={{
                position: 'relative',
                pl: { xs: 6, sm: 8 },
                mb: 4,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: { xs: 14, sm: 22 },
                  top: 32,
                  height: 'calc(100% - 32px)',
                  width: 2,
                  backgroundColor: 'divider',
                  zIndex: 1,
                },
                '&:last-child::before': {
                  display: 'none',
                }
              }}>
                <Box sx={{
                  position: 'absolute',
                  left: { xs: 8, sm: 16 },
                  top: 0,
                  zIndex: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: '50%',
                }}>
                  {getStatusIcon(event.status)}
                </Box>

                <Card sx={{
                  boxShadow: theme.shadows[3],
                  borderRadius: 4,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[6],
                  }
                }}>
                  <CardContent>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}>
                      <Typography variant="h6" component="h3">
                        {event.objective}
                      </Typography>
                      <Chip 
                        label={event.status} 
                        color={
                          event.status.toLowerCase() === 'complete' ? 'success' : 
                          event.status.toLowerCase() === 'in progress' ? 'warning' : 'default'
                        }
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(event.projected_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2,
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Check your creator portal for updates
                    </Typography>
                    <Button 
                      variant="contained" 
                      size="small"
                      onClick={() => navigate(`/offer/${creatorId}/${campaignId}`)}
                    >
                      View Full Offer
                    </Button>
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default CreatorCampaignTimelines;